import React, { useState, useMemo } from "react";
import { db } from "../firebase";
import {
  doc,
  updateDoc,
  arrayRemove,
  getDoc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { suggestDuplicates, nameKey } from "../utils/names";
import { runNameMerge } from "../utils/mergeNames";

const KIND_LABEL = { operators: "Operadores CCTV", guards: "Guardias" };

/**
 * NameManagerSection — sección del modal de Ajustes para gestionar nombres
 * de operadores y guardias: fusionar variantes duplicadas (reescribiendo el
 * histórico), renombrar y eliminar de la lista.
 *
 * Multi-recinto: en clientes donde el supervisor tiene varios recintos (ej.
 * Copec), opera sobre la UNIÓN de las listas de todos sus recintos y la
 * fusión limpia cada doc afectado.
 *
 * Los nombres mostrados son la unión de (a) las listas de los recintos y
 * (b) los nombres que aparecen en los registros de alarmas cargados —
 * históricamente muchos operadores nunca entraron a la lista del recinto
 * (solo el cierre persistía nombres), pero sí figuran en los registros.
 *
 * Props:
 *   facilities: array de docs de recinto (con id, operators, guards, nameAliases, devUID)
 *   zonesIds: Array<string|number> — ids de zonas de TODOS esos recintos
 *   alarms: array de alarmas cargadas (para derivar nombres de los registros)
 *   isSupervisor: bool
 *   isDev: bool
 *   user: { uid, email }
 */
export default function NameManagerSection({ facilities, zonesIds, alarms, isSupervisor, isDev, user }) {
  const [kind, setKind] = useState("operators");
  const [selected, setSelected] = useState([]);   // nombres seleccionados (valores exactos)
  const [canonical, setCanonical] = useState(""); // nombre final elegido/escrito
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState(null); // {phase, chunk, totalChunks, scanned, updated}
  const [result, setResult] = useState(null);     // {scannedDocs, updatedDocs, facilitiesTouched} | null
  const [error, setError] = useState(null);
  const [showAliases, setShowAliases] = useState(false);
  const [mergeLog, setMergeLog] = useState(null); // últimos merges (bloque dev)

  const facs = useMemo(() => (Array.isArray(facilities) ? facilities.filter(Boolean) : []), [facilities]);
  const canEdit = isSupervisor || isDev;
  const running = !!progress && progress.phase !== "done";

  // Unión de: listas de todos los recintos + nombres vistos en los registros
  // de alarmas cargados (sin repetir strings exactos; las variantes de
  // escritura se conservan para poder fusionarlas).
  const list = useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (n) => {
      if (n && typeof n === "string" && n.trim() && !seen.has(n)) { seen.add(n); out.push(n); }
    };
    for (const f of facs) {
      const arr = Array.isArray(f?.[kind]) ? f[kind] : [];
      arr.forEach(push);
    }
    for (const a of Array.isArray(alarms) ? alarms : []) {
      if (kind === "operators") {
        push(a.dispatchedBy);
        push(a.closedBy);
      } else {
        (Array.isArray(a.guards) ? a.guards : []).forEach(push);
        push(a.guardName);
      }
    }
    return out.sort((a, b) => a.localeCompare(b, "es"));
  }, [facs, kind, alarms]);

  const suggestions = useMemo(() => suggestDuplicates(list).slice(0, 5), [list]);

  // Aliases agregados de todos los recintos (para el bloque dev)
  const aliasMap = useMemo(() => {
    const out = {};
    for (const f of facs) {
      Object.assign(out, f?.nameAliases?.[kind] || {});
    }
    return out;
  }, [facs, kind]);

  const toggleName = (n) => {
    if (running) return;
    setResult(null); setError(null);
    setSelected((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));
  };

  const startMerge = () => {
    if (selected.length === 0) return;
    setCanonical(selected[0]);
    setConfirming(true);
    setResult(null); setError(null);
  };

  const cancelMerge = () => {
    setConfirming(false);
    setCanonical("");
  };

  const executeMerge = async () => {
    setError(null);
    setResult(null);
    setProgress({ phase: "scan", chunk: 0, totalChunks: 0, scanned: 0, updated: 0 });
    try {
      const res = await runNameMerge({
        facilityIds: facs.map((f) => f.id),
        zonesIds,
        kind,
        variants: selected,
        canonical,
        performedBy: { uid: user?.uid || null, name: user?.email || null },
        onProgress: setProgress,
        source: "ui",
      });
      setResult(res);
      setSelected([]);
      setConfirming(false);
      setCanonical("");
    } catch (e) {
      console.error("NameManagerSection merge error:", e);
      setError("La fusión falló a mitad de camino. Es seguro reintentar: los registros ya migrados no se vuelven a tocar.");
    } finally {
      setProgress((p) => (p ? { ...p, phase: "done" } : null));
    }
  };

  const removeFromList = async (n) => {
    if (!canEdit || running) return;
    try {
      // Quitar el string exacto de cada recinto que lo contenga
      for (const f of facs) {
        const arr = Array.isArray(f?.[kind]) ? f[kind] : [];
        if (arr.includes(n)) {
          await updateDoc(doc(db, `Facilities/${f.id}`), { [kind]: arrayRemove(n) });
        }
      }
      setSelected((s) => s.filter((x) => x !== n));
    } catch (e) {
      console.error("No se pudo eliminar de la lista", e);
    }
  };

  const removeAlias = async (key) => {
    if (!isDev || running) return;
    try {
      for (const f of facs) {
        const current = f?.nameAliases || {};
        if (!(current[kind] && key in current[kind])) continue;
        // Releer fresco para no pisar cambios de otros clientes
        const snap = await getDoc(doc(db, `Facilities/${f.id}`));
        const fresh = snap.exists() ? snap.data().nameAliases || {} : {};
        const next = { ...fresh, [kind]: { ...(fresh[kind] || {}) } };
        delete next[kind][key];
        await updateDoc(doc(db, `Facilities/${f.id}`), { nameAliases: next });
      }
    } catch (e) {
      console.error("No se pudo eliminar el alias", e);
    }
  };

  const loadMergeLog = async () => {
    try {
      const all = [];
      for (const f of facs) {
        const q = query(
          collection(db, `Facilities/${f.id}/mergeLog`),
          orderBy("createdAt", "desc"),
          limit(5)
        );
        const snap = await getDocs(q);
        snap.docs.forEach((d) => all.push({ id: `${f.id}-${d.id}`, facilityTitle: f.title || f.id, ...d.data() }));
      }
      all.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setMergeLog(all.slice(0, 10));
    } catch (e) {
      console.error("No se pudo cargar mergeLog", e);
      setMergeLog([]);
    }
  };

  const sub = { fontSize: 9, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 };
  const btn = (bg, color = "#fff") => ({
    padding: "6px 10px", border: "none", borderRadius: 6,
    background: bg, color, fontSize: 10, fontWeight: 700, cursor: "pointer",
  });

  return (
    <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
          Nombres de Personal
        </span>
        {/* Tabs Operadores / Guardias */}
        <div style={{ display: "flex", gap: 2, background: "var(--surface)", borderRadius: 6, padding: 2 }}>
          {["operators", "guards"].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => { if (!running) { setKind(k); setSelected([]); setConfirming(false); setResult(null); setError(null); } }}
              style={{
                padding: "3px 8px", borderRadius: 4, border: "none",
                background: kind === k ? "var(--bg)" : "transparent",
                color: kind === k ? "var(--bright)" : "var(--muted)",
                fontSize: 9, fontWeight: 700, cursor: "pointer",
              }}
            >{KIND_LABEL[k]}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 9.5, color: "var(--muted)", marginBottom: 8, lineHeight: 1.4 }}>
        Selecciona los nombres que correspondan a la misma persona y fusiónalos en uno solo.
        La fusión también corrige los registros históricos{facs.length > 1 ? ` de tus ${facs.length} recintos` : " de este recinto"}.
      </div>

      {/* Sugerencias de duplicados */}
      {suggestions.length > 0 && !confirming && (
        <div style={{ marginBottom: 8, display: "flex", flexDirection: "column", gap: 4 }}>
          {suggestions.map((s, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6,
              background: "#f59e0b10", border: "1px solid #f59e0b30", borderRadius: 6,
              padding: "5px 8px", fontSize: 9.5, color: "#fbbf24",
            }}>
              <span>¿"{s.a}" y "{s.b}" son la misma persona? <span style={{ opacity: 0.7 }}>({s.reason})</span></span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { setSelected([s.a, s.b]); setResult(null); setError(null); }}
                  style={btn("#f59e0b25", "#fbbf24")}
                >Seleccionar</button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Lista de nombres (unión de todos los recintos) */}
      {list.length === 0 ? (
        <div style={{ fontSize: 10, color: "var(--muted)", padding: "8px 0" }}>Sin {KIND_LABEL[kind].toLowerCase()} registrados.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
          {list.map((n, i) => {
            const sel = selected.includes(n);
            return (
              <button
                key={`${n}-${i}`}
                type="button"
                onClick={() => canEdit && toggleName(n)}
                title={canEdit ? "Seleccionar para fusionar/renombrar" : undefined}
                style={{
                  padding: "5px 9px", borderRadius: 6,
                  border: `1.5px solid ${sel ? "#3b82f6" : "var(--border)"}`,
                  background: sel ? "#3b82f615" : "var(--surface)",
                  color: sel ? "#3b82f6" : "var(--subtle)",
                  fontSize: 10, fontWeight: 600, cursor: canEdit ? "pointer" : "default",
                }}
              >{sel ? "☑ " : ""}{n}</button>
            );
          })}
        </div>
      )}

      {/* Acciones */}
      {canEdit && selected.length > 0 && !confirming && !running && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" onClick={startMerge} style={btn("#3b82f6")}>
            {selected.length >= 2 ? `Fusionar ${selected.length} nombres` : "Renombrar"}
          </button>
          {selected.length === 1 && (
            <button type="button" onClick={() => removeFromList(selected[0])} style={btn("var(--border)", "var(--subtle)")}>
              Quitar de la lista
            </button>
          )}
          <button type="button" onClick={() => setSelected([])} style={btn("transparent", "var(--muted)")}>
            Limpiar selección
          </button>
        </div>
      )}

      {/* Confirmación */}
      {confirming && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 7, padding: 10, marginTop: 6 }}>
          <div style={sub}>Nombre final</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
            {selected.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setCanonical(n)}
                style={{
                  padding: "4px 8px", borderRadius: 5,
                  border: `1.5px solid ${canonical === n ? "#22c55e" : "var(--border)"}`,
                  background: canonical === n ? "#22c55e15" : "transparent",
                  color: canonical === n ? "#22c55e" : "var(--subtle)",
                  fontSize: 10, cursor: "pointer",
                }}
              >{n}</button>
            ))}
          </div>
          <input
            className="v3-input"
            value={canonical}
            onChange={(e) => setCanonical(e.target.value)}
            placeholder="O escribe el nombre correcto…"
            style={{ marginBottom: 8 }}
          />
          <div style={{ fontSize: 9.5, color: "#fbbf24", marginBottom: 8, lineHeight: 1.4 }}>
            ⚠️ {selected.filter((n) => n !== canonical).length > 0
              ? `"${selected.filter((n) => n !== canonical).join('", "')}" pasará(n) a ser "${canonical || "…"}".`
              : `Se normalizará la escritura de "${canonical || "…"}".`} Se actualizarán
            los registros históricos{facs.length > 1 ? ` de tus ${facs.length} recintos` : " de este recinto"}. Esta acción no se puede deshacer automáticamente.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              disabled={!canonical.trim() || running}
              onClick={executeMerge}
              style={{ ...btn(canonical.trim() ? "#22c55e" : "var(--border)"), cursor: canonical.trim() ? "pointer" : "not-allowed" }}
            >Confirmar fusión</button>
            <button type="button" onClick={cancelMerge} disabled={running} style={btn("transparent", "var(--muted)")}>Cancelar</button>
          </div>
        </div>
      )}

      {/* Progreso / resultado / error */}
      {running && progress && (
        <div style={{ marginTop: 8, fontSize: 10, color: "var(--subtle)", fontFamily: "var(--mono)" }}>
          ⏳ Revisando registros… {progress.totalChunks > 0 ? `zona ${progress.chunk}/${progress.totalChunks} · ` : ""}
          {progress.scanned} revisados · {progress.updated} corregidos
        </div>
      )}
      {result && (
        <div style={{ marginTop: 8, fontSize: 10, color: "#22c55e", fontWeight: 600 }}>
          ✓ Fusión completada: {result.updatedDocs} registros corregidos (de {result.scannedDocs} revisados{result.facilitiesTouched > 1 ? ` · ${result.facilitiesTouched} recintos` : ""}).
        </div>
      )}
      {error && (
        <div style={{ marginTop: 8, fontSize: 10, color: "#ef4444" }}>
          {error}{" "}
          <button type="button" onClick={executeMerge} style={btn("#ef444425", "#ef4444")}>Reintentar</button>
        </div>
      )}

      {/* Bloque avanzado (solo dev interno Tank) */}
      {isDev && (
        <div style={{ marginTop: 10, borderTop: "1px dashed var(--border)", paddingTop: 8 }}>
          <button
            type="button"
            onClick={() => { setShowAliases(!showAliases); if (!showAliases && mergeLog === null) loadMergeLog(); }}
            style={btn("transparent", "var(--muted)")}
          >{showAliases ? "▲" : "▼"} Avanzado (aliases y auditoría)</button>
          {showAliases && (
            <div style={{ marginTop: 6 }}>
              <div style={sub}>Aliases activos — {KIND_LABEL[kind]}</div>
              {Object.keys(aliasMap).length === 0 ? (
                <div style={{ fontSize: 9.5, color: "var(--muted)" }}>Sin aliases.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 8 }}>
                  {Object.entries(aliasMap).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9.5, fontFamily: "var(--mono)", color: "var(--subtle)" }}>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>"{k}" → {v}</span>
                      <button type="button" onClick={() => removeAlias(k)} title="Eliminar alias" style={btn("transparent", "#ef4444")}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={sub}>Últimas fusiones</div>
              {mergeLog === null ? (
                <div style={{ fontSize: 9.5, color: "var(--muted)" }}>Cargando…</div>
              ) : mergeLog.length === 0 ? (
                <div style={{ fontSize: 9.5, color: "var(--muted)" }}>Sin fusiones registradas.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {mergeLog.map((m) => (
                    <div key={m.id} style={{ fontSize: 9.5, color: "var(--muted)", lineHeight: 1.35 }}>
                      <b style={{ color: "var(--subtle)" }}>{KIND_LABEL[m.kind] || m.kind}</b>: {(m.variants || []).join(", ")} → <b style={{ color: "var(--subtle)" }}>{m.canonical}</b>
                      {" "}({m.updatedDocs} regs · {m.performedByName || m.performedByUID || "?"}
                      {facs.length > 1 && m.facilityTitle ? ` · ${m.facilityTitle}` : ""}
                      {m.createdAt?.seconds ? ` · ${new Date(m.createdAt.seconds * 1000).toLocaleDateString("es-CL")}` : ""})
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
